<template>
  <div class="ts-page">
    <div class="ts-page-header">
      <div class="ts-page-title">Export Contabilità</div>
      <div class="ts-page-subtitle">
        Storico export per <b>Incassi</b>, <b>Vendite</b> e <b>Movimenti</b>.
      </div>
    </div>

    <div v-if="loadingAny" class="ts-global-loader">
      <Loader />
    </div>

    <div class="ts-tabs-wrap">
      <div class="ts-tabs-title">Seleziona una sezione</div>

      <div class="ts-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="p-button p-button-info ts-btn ts-tab"
          type="button"
          :class="{ 'ts-tab-active': activeTab === tab.key }"
          :disabled="loading[tab.key]"
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="ts-tabs-hint">
        Seleziona il tab per visualizzare lo storico export e scaricare i file.
      </div>
    </div>

    <div class="ts-panel">
      <div class="ts-card">
        <div class="ts-card-header">
          <div>
            <div class="ts-card-title">{{ activeTabConfig.label }}</div>
            <div class="ts-card-desc">{{ activeTabConfig.description }}</div>
          </div>

          <div class="ts-card-actions">
            <button
              class="p-button p-button-info ts-btn ts-btn-small ts-btn-ghost"
              type="button"
              :disabled="currentLoading"
              @click="reloadCurrentTab"
            >
              Ricarica
            </button>
          </div>
        </div>

        <div class="ts-filters-wrap">
          <div class="ts-filters-grid">
            <div class="ts-filter-item">
              <label class="ts-filter-label">Nome file</label>
              <input
                v-model="currentFilters.filename"
                class="ts-input"
                type="text"
                placeholder="Cerca filename"
              >
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Banca</label>
              <input
                v-model="currentFilters.banca"
                class="ts-input"
                type="text"
                placeholder="Es. BPPB"
              >
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Fattura</label>
              <input
                v-model="currentFilters.fattura"
                class="ts-input"
                type="text"
                placeholder="Numero fattura"
              >
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Scaricato</label>
              <select v-model="currentFilters.downloaded" class="ts-input">
                <option value="">Tutti</option>
                <option :value="1">Sì</option>
                <option :value="0">No</option>
              </select>
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Cron</label>
              <select v-model="currentFilters.is_cron" class="ts-input">
                <option value="">Tutti</option>
                <option :value="1">Sì</option>
                <option :value="0">No</option>
              </select>
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Data dal</label>
              <input
                v-model="currentFilters.data_dal"
                class="ts-input"
                type="date"
              >
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Data al</label>
              <input
                v-model="currentFilters.data_al"
                class="ts-input"
                type="date"
              >
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Creazione dal</label>
              <input
                v-model="currentFilters.creation_time_dal"
                class="ts-input"
                type="date"
              >
            </div>

            <div class="ts-filter-item">
              <label class="ts-filter-label">Creazione al</label>
              <input
                v-model="currentFilters.creation_time_al"
                class="ts-input"
                type="date"
              >
            </div>
          </div>

          <div class="ts-filters-actions">
            <button
              class="p-button p-button-info ts-btn ts-btn-small"
              type="button"
              :disabled="currentLoading"
              @click="applyFilters"
            >
              Applica filtri
            </button>

            <button
              class="p-button p-button-info ts-btn ts-btn-small ts-btn-ghost"
              type="button"
              :disabled="currentLoading"
              @click="resetFilters"
            >
              Reset
            </button>
          </div>
        </div>

        <div class="ts-card-body ts-card-body-nopad">
          <div v-if="currentLoading" class="ts-section-loader">
            <Loader />
          </div>

          <div class="ts-table-wrap ts-table-wrap-noradius">
            <table class="ts-table">
              <thead>
                <tr>
                  <th>Nome file</th>
                  <th>Tipo</th>
                  <th>Da</th>
                  <th>Al</th>
                  <th>Banca</th>
                  <th>Fattura</th>
                  <th>Part.</th>
                  <th>Righe</th>
                  <th>Size</th>
                  <th>Ext</th>
                  <th>Download</th>
                  <th>Cron</th>
                  <th>Data creazione</th>
                  <th style="text-align: right !important;">Azioni</th>
                </tr>
              </thead>

              <tbody>
                <tr v-if="!currentLoading && currentRows.length === 0">
                  <td class="ts-empty" colspan="14">Nessun export disponibile.</td>
                </tr>

                <tr v-for="row in currentRows" :key="getRowKey(row)">
                  <td class="ts-mono">{{ getFileName(row) }}</td>
                  <td>{{ row.tipo || '-' }}</td>
                  <td>{{ formatDate(getDateFrom(row)) }}</td>
                  <td>{{ formatDate(getDateTo(row)) }}</td>
                  <td>{{ row.banca || '-' }}</td>
                  <td>{{ row.fattura || '-' }}</td>
                  <td>{{ row.partitario || '-' }}</td>
                  <td>{{ row.file_rows ?? 0 }}</td>
                  <td>{{ formatSize(row.file_size) }}</td>
                  <td>{{ row.ext || '-' }}</td>
                  <td>
                    <span
                      class="ts-badge"
                      :class="row.downloaded ? 'ts-badge-success' : 'ts-badge-muted'"
                      :title="row.downloaded ? 'File già scaricato' : 'File non ancora scaricato'"
                    >
                      {{ row.downloaded ? '✔ Scaricato' : 'Non scaricato' }}
                    </span>
                  </td>

                  <td>
                    <span
                      class="ts-badge"
                      :class="row.is_cron ? 'ts-badge-info' : 'ts-badge-muted'"
                      :title="row.is_cron ? 'Generato da processo automatico' : 'Generato manualmente'"
                    >
                      {{ row.is_cron ? '⚙ Cron' : 'Manuale' }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(getCreatedAt(row)) }}</td>
                  <td style="text-align: right !important;">
                    <button
                      class="p-button p-button-info ts-btn ts-btn-small"
                      type="button"
                      :disabled="currentLoading"
                      @click="downloadCurrentRow(row)"
                    >
                      Scarica
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="ts-pagination-wrap">
            <div class="ts-pagination-info">
              Totale: <b>{{ currentPagination.total || 0 }}</b>
              <span v-if="currentPagination.from && currentPagination.to">
                | Visualizzati {{ currentPagination.from }} - {{ currentPagination.to }}
              </span>
            </div>

            <div class="ts-pagination-actions">
              <button
                class="p-button p-button-info ts-btn ts-btn-small ts-btn-ghost"
                type="button"
                :disabled="currentLoading || currentPagination.current_page <= 1"
                @click="changePage(currentPagination.current_page - 1)"
              >
                Precedente
              </button>

              <span class="ts-pagination-page">
                Pagina {{ currentPagination.current_page || 1 }} di {{ currentPagination.last_page || 1 }}
              </span>

              <button
                class="p-button p-button-info ts-btn ts-btn-small ts-btn-ghost"
                type="button"
                :disabled="currentLoading || currentPagination.current_page >= currentPagination.last_page"
                @click="changePage(currentPagination.current_page + 1)"
              >
                Successiva
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '@/components/Loader.vue'
import ExportContabilitaService from '@/services/contabilita/export-contabilita-service.js'

function getDefaultFilters() {
  return {
    filename: '',
    banca: '',
    fattura: '',
    downloaded: '',
    is_cron: '',
    data_dal: '',
    data_al: '',
    creation_time_dal: '',
    creation_time_al: '',
  }
}

function getDefaultPagination() {
  return {
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0,
    from: 0,
    to: 0,
  }
}

export default {
  name: 'ExportContabilitaPage',
  components: { Loader },

  data() {
    return {
      service: null,
      activeTab: 'incassi',
      tabs: [
        {
          key: 'incassi',
          label: 'Incassi',
          description: 'Elenco file export incassi disponibili al download.',
        },
        {
          key: 'vendite',
          label: 'Vendite',
          description: 'Elenco file export vendite disponibili al download.',
        },
        {
          key: 'movimenti',
          label: 'Movimenti',
          description: 'Elenco file export movimenti disponibili al download.',
        },
      ],
      rows: {
        incassi: [],
        vendite: [],
        movimenti: [],
      },
      loading: {
        incassi: false,
        vendite: false,
        movimenti: false,
      },
      loaded: {
        incassi: false,
        vendite: false,
        movimenti: false,
      },
      filters: {
        incassi: getDefaultFilters(),
        vendite: getDefaultFilters(),
        movimenti: getDefaultFilters(),
      },
      pagination: {
        incassi: getDefaultPagination(),
        vendite: getDefaultPagination(),
        movimenti: getDefaultPagination(),
      },
    }
  },

  computed: {
    loadingAny() {
      return this.loading.incassi || this.loading.vendite || this.loading.movimenti
    },

    activeTabConfig() {
      return this.tabs.find((tab) => tab.key === this.activeTab) || this.tabs[0]
    },

    currentRows() {
      return this.rows[this.activeTab] || []
    },

    currentLoading() {
      return this.loading[this.activeTab]
    },

    currentFilters() {
      return this.filters[this.activeTab]
    },

    currentPagination() {
      return this.pagination[this.activeTab] || getDefaultPagination()
    },
  },

  created() {
    this.service = new ExportContabilitaService(this.$http, this.$eventBus)
  },

  async mounted() {
    await this.ensureTabLoaded(this.activeTab)
  },

  methods: {
    async selectTab(tabKey) {
      if (this.activeTab === tabKey) {
        return
      }

      this.activeTab = tabKey
      await this.ensureTabLoaded(tabKey)
    },

    async ensureTabLoaded(type) {
      if (this.loaded[type]) {
        return
      }

      await this.fetchRows(type)
    },

    async reloadCurrentTab() {
      await this.fetchRows(this.activeTab, { force: true, page: this.currentPagination.current_page })
    },

    async applyFilters() {
      this.pagination[this.activeTab].current_page = 1
      await this.fetchRows(this.activeTab, { force: true, page: 1 })
    },

    async resetFilters() {
      this.filters[this.activeTab] = getDefaultFilters()
      this.pagination[this.activeTab].current_page = 1
      await this.fetchRows(this.activeTab, { force: true, page: 1 })
    },

    async changePage(page) {
      if (page < 1) {
        return
      }

      await this.fetchRows(this.activeTab, { force: true, page })
    },

    async fetchRows(type, options = {}) {
      const { force = false, page = 1 } = options

      if (this.loading[type]) {
        return
      }

      if (this.loaded[type] && !force) {
        return
      }

      this.loading[type] = true

      try {
        const response = await this.callListMethod(type, {
          ...this.filters[type],
          page,
          rows: this.pagination[type].per_page || 25,
        })

        this.rows[type] = Array.isArray(response?.data) ? response.data : []
        this.pagination[type] = {
          current_page: response?.current_page || 1,
          last_page: response?.last_page || 1,
          per_page: Number(response?.per_page || 25),
          total: Number(response?.total || 0),
          from: Number(response?.from || 0),
          to: Number(response?.to || 0),
        }
        this.loaded[type] = true
      } catch (error) {
        this.rows[type] = []
        this.pagination[type] = getDefaultPagination()
        console.error(`Errore caricamento export ${type}:`, error)
      } finally {
        this.loading[type] = false
      }
    },

    async callListMethod(type, params = {}) {
      const methodMap = {
        incassi: 'getIncassi',
        vendite: 'getVendite',
        movimenti: 'getMovimenti',
      }

      const methodName = methodMap[type]

      if (!methodName || typeof this.service?.[methodName] !== 'function') {
        throw new Error(`Metodo service non disponibile per il tipo "${type}"`)
      }

      return await this.service[methodName](params)
    },

    async downloadCurrentRow(row) {
      await this.downloadFile(this.activeTab, row)
    },

    async downloadFile(type, row) {
      if (this.loading[type]) {
        return
      }

      this.loading[type] = true

      try {
        if (typeof this.service?.downloadFile !== 'function') {
          throw new Error('Metodo downloadFile non disponibile nel service')
        }

        await this.service.downloadFile(row)
      } catch (error) {
        console.error(`Errore download file ${type}:`, error)
      } finally {
        this.loading[type] = false
      }

      await this.fetchRows(type, {
        force: true,
        page: this.pagination[type].current_page || 1,
      })
    },

    getRowKey(row) {
      return [
        this.activeTab,
        row?.id ?? '',
        this.getFileName(row),
        this.getCreatedAt(row),
      ].join('-')
    },

    getFileName(row) {
      return row?.filename || row?.nome_file || row?.file_name || '-'
    },

    getDateFrom(row) {
      return row?.data_dal || row?.data_da || row?.from || row?.date_from || null
    },

    getDateTo(row) {
      return row?.data_al || row?.data_a || row?.al || row?.to || row?.date_to || null
    },

    getCreatedAt(row) {
      return row?.creation_time || row?.data_creazione || row?.created_at || row?.uploaded_at || null
    },

    formatDate(value) {
      if (!value) {
        return '-'
      }

      const stringValue = String(value).trim()

      if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
        const [year, month, day] = stringValue.split('-')
        return `${day}/${month}/${year}`
      }

      if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(stringValue)) {
        const [datePart] = stringValue.split(' ')
        const [year, month, day] = datePart.split('-')
        return `${day}/${month}/${year}`
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleDateString('it-IT')
    },

    formatDateTime(value) {
      if (!value) {
        return '-'
      }

      const stringValue = String(value).trim()

      if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(stringValue)) {
        const [datePart, timePart] = stringValue.split(' ')
        const [year, month, day] = datePart.split('-')
        return `${day}/${month}/${year} ${timePart}`
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleString('it-IT')
    },

    formatSize(bytes) {
      if (!bytes) {
        return '-'
      }

      const sizes = ['B', 'KB', 'MB', 'GB']
      let value = Number(bytes)
      let index = 0

      while (value >= 1024 && index < sizes.length - 1) {
        value /= 1024
        index++
      }

      return `${value.toFixed(1)} ${sizes[index]}`
    },
  },
}
</script>

<style scoped>
.ts-page { padding: 18px !important; }
.ts-page-header { margin-bottom: 14px !important; }
.ts-page-title { font-size: 22px !important; font-weight: 800 !important; margin: 0 !important; }
.ts-page-subtitle { margin-top: 6px !important; font-size: 13px !important; opacity: 0.9 !important; }

.ts-btn { border-radius: 10px !important; }
.ts-btn-small { padding: 0.45rem 0.75rem !important; font-size: 0.85rem !important; }
.ts-btn-ghost { background: transparent !important; color: #0b66a7 !important; border: 1px solid rgba(6, 141, 224, 0.55) !important; }

.ts-tabs-wrap {
  background: #ffffff !important;
  border: 2px solid rgba(17, 24, 39, 0.18) !important;
  border-radius: 12px !important;
  padding: 16px !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.10) !important;
  margin-bottom: 14px !important;
}

.ts-tabs-title { font-weight: 900 !important; font-size: 14px !important; margin-bottom: 10px !important; }
.ts-tabs { display: flex !important; gap: 10px !important; flex-wrap: wrap !important; }
.ts-tab { padding: 0.65rem 1rem !important; font-weight: 800 !important; }
.ts-tab-active {
  box-shadow: inset 0 0 0 2px rgba(17, 24, 39, 0.18) !important;
  filter: brightness(0.95) !important;
}
.ts-tabs-hint { margin-top: 10px !important; font-size: 12px !important; opacity: 0.85 !important; }

.ts-panel {
  display: grid !important;
  gap: 14px !important;
}

.ts-card {
  position: relative !important;
  background: #ffffff !important;
  border: 2px solid rgba(17, 24, 39, 0.18) !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.10) !important;
}

.ts-card-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 14px !important;
  border-bottom: 2px solid rgba(17, 24, 39, 0.12) !important;
  background: rgba(6, 141, 224, 0.10) !important;
  gap: 12px !important;
}

.ts-card-actions {
  display: flex !important;
  gap: 8px !important;
  align-items: center !important;
  flex-wrap: wrap !important;
}

.ts-card-title { font-size: 16px !important; font-weight: 800 !important; }
.ts-card-desc { margin-top: 4px !important; font-size: 12px !important; opacity: 0.85 !important; }

.ts-card-body { padding: 14px !important; }
.ts-card-body-nopad { padding: 0 !important; }

.ts-filters-wrap {
  padding: 14px !important;
  border-bottom: 2px solid rgba(17, 24, 39, 0.12) !important;
  background: #f8fbfe !important;
}

.ts-filters-grid {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  gap: 12px !important;
}

.ts-filter-item {
  display: flex !important;
  flex-direction: column !important;
  gap: 6px !important;
}

.ts-filter-label {
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
}

.ts-input {
  width: 100% !important;
  min-height: 38px !important;
  border: 1px solid rgba(17, 24, 39, 0.18) !important;
  border-radius: 8px !important;
  padding: 0.55rem 0.7rem !important;
  font-size: 13px !important;
  background: #fff !important;
}

.ts-filters-actions {
  display: flex !important;
  gap: 8px !important;
  margin-top: 12px !important;
  justify-content: flex-end !important;
}

.ts-table-wrap {
  overflow: auto !important;
  border-radius: 10px !important;
  border: 2px solid rgba(17, 24, 39, 0.16) !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06) !important;
}

.ts-table-wrap-noradius { border-radius: 0 !important; }

.ts-table {
  width: 100% !important;
  border-collapse: collapse !important;
  font-size: 13px !important;
  background: #fff !important;
  min-width: 1500px !important;
}

.ts-table thead th {
  background: #068de0 !important;
  color: #fff !important;
  font-weight: 800 !important;
  font-size: 12px !important;
  padding: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.02em !important;
  border-right: 1px solid rgba(255, 255, 255, 0.25) !important;
  user-select: none !important;
}

.ts-table thead th:last-child { border-right: none !important; }

.ts-table td {
  padding: 10px !important;
  border-bottom: 1px solid rgba(17, 24, 39, 0.14) !important;
}

.ts-table tbody tr:hover td {
  background: rgba(6, 141, 224, 0.07) !important;
}

.ts-empty {
  text-align: center !important;
  padding: 16px !important;
  opacity: 0.85 !important;
}

.ts-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
}

.ts-pagination-wrap {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  gap: 12px !important;
  padding: 14px !important;
  border-top: 2px solid rgba(17, 24, 39, 0.12) !important;
  background: #f8fbfe !important;
  flex-wrap: wrap !important;
}

.ts-pagination-info {
  font-size: 13px !important;
}

.ts-pagination-actions {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  flex-wrap: wrap !important;
}

.ts-pagination-page {
  font-size: 13px !important;
  font-weight: 700 !important;
}

.ts-global-loader {
  position: fixed !important;
  inset: 0 !important;
  background: rgba(255, 255, 255, 0.55) !important;
  display: grid !important;
  place-items: center !important;
  z-index: 999998 !important;
}

.ts-section-loader {
  position: absolute !important;
  inset: 0 !important;
  background: rgba(255, 255, 255, 0.75) !important;
  display: grid !important;
  place-items: center !important;
  z-index: 50 !important;
}

.ts-badge {
  display: inline-block !important;
  padding: 3px 8px !important;
  border-radius: 999px !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  white-space: nowrap !important;
}

.ts-badge-success {
  background: rgba(34, 197, 94, 0.15) !important;
  color: #15803d !important;
}

.ts-badge-info {
  background: rgba(6, 141, 224, 0.15) !important;
  color: #0b66a7 !important;
}

.ts-badge-muted {
  background: rgba(17, 24, 39, 0.08) !important;
  color: #6b7280 !important;
}

@media (max-width: 1100px) {
  .ts-card-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .ts-card-actions {
    justify-content: flex-end !important;
  }

  .ts-filters-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}

@media (max-width: 700px) {
  .ts-filters-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
  }
}
</style>